import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Share2, X, Check, Copy, Twitter, Facebook, Linkedin, MessageCircle } from 'lucide-react';

const SocialShare = ({ 
  title, 
  text, 
  url, 
  hashtags = ['ADACI', 'CotedIvoire'],
  compact = false 
}) => {
  const { isDark } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const shareText = text || title;
  const hashtagString = hashtags.map(t => `#${t}`).join(' ');

  const shareLinks = [
    {
      name: 'Twitter',
      icon: Twitter,
      color: 'bg-[#1DA1F2] hover:bg-[#0d8bd9]',
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}&hashtags=${hashtags.join(',')}`
    },
    {
      name: 'Facebook',
      icon: Facebook,
      color: 'bg-[#1877F2] hover:bg-[#0d65d9]',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      color: 'bg-[#0A66C2] hover:bg-[#084d94]',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
    },
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      color: 'bg-[#25D366] hover:bg-[#1da851]',
      url: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`
    }
  ];

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText}\n\n${shareUrl}\n\n${hashtagString}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleShare = (link) => {
    window.open(link.url, '_blank', 'width=600,height=400');
  };

  // Use native share if available on mobile
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: shareText,
          url: shareUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          setIsOpen(true);
        }
      }
    } else {
      setIsOpen(true);
    }
  };

  if (compact) {
    return (
      <div className="relative inline-block">
        <button
          onClick={handleNativeShare}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
            isDark 
              ? 'bg-white/10 text-stone-300 hover:bg-white/20' 
              : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
          }`}
          data-testid="share-button"
        >
          <Share2 className="w-3.5 h-3.5" />
          Partager
        </button>

        <AnimatePresence>
          {isOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-40"
                onClick={() => setIsOpen(false)}
              />
              
              {/* Share Menu */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -10 }}
                className={`absolute right-0 top-full mt-2 z-50 p-3 rounded-xl shadow-xl ${
                  isDark ? 'bg-[#292524] border border-white/10' : 'bg-white border border-stone-200'
                }`}
                style={{ minWidth: '200px' }}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-xs font-semibold ${isDark ? 'text-stone-400' : 'text-stone-500'}`}>
                    Partager sur
                  </span>
                  <button 
                    onClick={() => setIsOpen(false)}
                    className={`p-1 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-stone-100'}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
                
                <div className="flex gap-2 mb-3">
                  {shareLinks.map((link) => (
                    <button
                      key={link.name}
                      onClick={() => handleShare(link)}
                      className={`w-9 h-9 rounded-full flex items-center justify-center text-white transition-transform hover:scale-110 ${link.color}`}
                      title={link.name}
                      data-testid={`share-${link.name.toLowerCase()}`}
                    >
                      <link.icon className="w-4 h-4" />
                    </button>
                  ))}
                </div>
                
                <button
                  onClick={copyToClipboard}
                  className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                    copied
                      ? 'bg-green-500/20 text-green-500'
                      : isDark 
                        ? 'bg-white/10 text-stone-300 hover:bg-white/20' 
                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                  data-testid="copy-link-button"
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      Copié !
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      Copier le lien
                    </>
                  )}
                </button>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Full-size variant
  return (
    <div className={`rounded-xl p-4 ${isDark ? 'bg-[#292524]' : 'bg-stone-50'}`}>
      <div className="flex items-center gap-2 mb-3">
        <Share2 className={`w-4 h-4 ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`} />
        <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-stone-700'}`}>
          Partager cette position
        </span>
      </div>
      
      <div className="flex flex-wrap gap-2 mb-3">
        {shareLinks.map((link) => (
          <button
            key={link.name}
            onClick={() => handleShare(link)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-white text-xs font-medium transition-transform hover:scale-105 ${link.color}`}
            data-testid={`share-${link.name.toLowerCase()}`}
          >
            <link.icon className="w-4 h-4" />
            {link.name}
          </button>
        ))}
      </div>
      
      <button
        onClick={copyToClipboard}
        className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
          copied
            ? 'bg-green-500/20 text-green-500 border border-green-500/30'
            : isDark 
              ? 'bg-white/10 text-stone-300 hover:bg-white/20 border border-white/10' 
              : 'bg-white text-stone-600 hover:bg-stone-100 border border-stone-200'
        }`}
        data-testid="copy-link-button"
      >
        {copied ? (
          <>
            <Check className="w-4 h-4" />
            Lien copié dans le presse-papier !
          </>
        ) : (
          <>
            <Copy className="w-4 h-4" />
            Copier le lien
          </>
        )}
      </button>
    </div>
  );
};

export default SocialShare;
